import React, { Suspense, useCallback, useContext, useMemo } from 'react'
import { getPostedMessageType, trackPostedMessage } from '@components/Analytics/postedMessage'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { useInlineReplyAttchmentPreview } from '@components/EmbeddedMessageAttachement/hooks/useInlineReplyAttchmentPreview'
import { ReplyToMessageContext } from '@components/ReplyToMessageContext/ReplyToMessageContext'
import { Spinner } from '@components/Spinner'
import { IGif } from '@giphy/js-types'
import { ImageAttachment } from '@towns-protocol/sdk'
import { Box } from '@ui'
import { useDevice } from 'hooks/useDevice'
import {
    createEmbeddedMessageAttachment,
    useGetChannelEvent,
} from 'hooks/useExtractMessageAttachments'
import { useOpenMessageThread } from 'hooks/useOpenThread'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { useStore } from 'store/store'
import { useCardOpenerContext } from 'ui/components/Overlay/CardOpenerContext'
import { atoms } from 'ui/styles/atoms.css'
import { themes } from 'ui/styles/themes'
import { baseline } from 'ui/styles/vars.css'
import {
    SendTextMessageOptions,
    useChannelData,
    useChannelId,
    useTownsClient,
} from 'use-towns-client'

import './GiphyGrid.css'

import { GiphySearchBar } from './GiphySearchBar'
import { useGiphySearchContext } from './GiphySearchContext'

const Grid = React.lazy(() =>
    import('@giphy/react-components').then((module) => ({ default: module.Grid })),
)

const Loader = React.memo(() => {
    return (
        <Box centerContent paddingTop="md">
            <Spinner />
        </Box>
    )
})
Loader.displayName = 'Loader'

const ErrorFallback = React.memo(() => {
    return (
        <Box centerContent paddingY="md" color="secondary">
            Failed to load GIFs. Please try again later.
        </Box>
    )
})
ErrorFallback.displayName = 'ErrorFallback'

type Props = {
    threadId?: string
    threadPreview?: string
}

type GiphyPickerCardProps = Props & {
    closeCard: () => void
}

export const GiphyPicker = React.memo((props: Props) => {
    const { closeCard } = useCardOpenerContext()
    return <GiphyPickerCard {...props} closeCard={closeCard} />
})
GiphyPicker.displayName = 'GiphyPicker'

// Create GIF attachment from Giphy data
const createGifAttachment = (gifInfo: {
    url: string
    width: number
    height: number
}): ImageAttachment => ({
    type: 'image',
    info: {
        url: gifInfo.url,
        width: gifInfo.width,
        height: gifInfo.height,
        mimetype: 'image/gif',
    } as ImageAttachment['info'] & { mimetype: string },
    id: `gif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
})

export const GiphyPickerCard = React.memo((props: GiphyPickerCardProps) => {
    const theme = useStore((state) => state.getTheme())
    const { isTouch } = useDevice()
    const { closeCard, threadId, threadPreview } = props

    const { fetchGifs, query, isFetching } = useGiphySearchContext()
    const { sendMessage } = useTownsClient()
    const channelId = useChannelId()
    const { spaceId } = useChannelData()
    const spaceIdFromPath = useSpaceIdFromPathname()
    const gutterWidth = baseline * 0.75

    // Reply context
    const { replyToEventId, replyMode, canReplyInline, resetReplyToMessageContext } =
        useContext(ReplyToMessageContext)
    const { inlineReplyPreview } = useInlineReplyAttchmentPreview()
    const { getChannelEvent } = useGetChannelEvent()
    const { onOpenMessageThread } = useOpenMessageThread()

    const { width, gridWidth } = useMemo(() => {
        const width = isTouch ? window.innerWidth : 350
        const gridWidth = width - gutterWidth
        return { width, gridWidth }
    }, [isTouch, gutterWidth])

    const onGifClick = useCallback(
        (gifData: IGif) => {
            closeCard()

            // Create GIF attachment and send immediately as empty message
            const ogImage = gifData.images.original
            const gifAttachment = createGifAttachment({
                url: ogImage.url,
                width: Number(ogImage.width) || 0,
                height: Number(ogImage.height) || 0,
            })

            // Send empty message with GIF attachment and reply context
            const messageOptions: SendTextMessageOptions = {
                attachments: [gifAttachment],
            }

            // Store thread info for opening thread panel after send
            let shouldOpenThread = false
            let threadParentId: string | undefined

            // Apply thread and reply logic (same as useChannelSend)
            // First check if we're already in a thread context
            if (threadId && threadPreview) {
                // We're in a thread - send message to this thread
                messageOptions.threadId = threadId
                messageOptions.threadPreview = threadPreview
            } else if (replyMode === 'inline' && inlineReplyPreview) {
                const event = getChannelEvent(channelId, inlineReplyPreview.event.eventId)
                if (event) {
                    const attachment = createEmbeddedMessageAttachment({
                        url: '',
                        spaceId: spaceId ?? spaceIdFromPath ?? '',
                        channelId: channelId,
                        messageId: inlineReplyPreview.event.eventId,
                        userId: inlineReplyPreview.event.sender.id,
                        event: inlineReplyPreview.event,
                    })

                    messageOptions.attachments = [...(messageOptions.attachments || []), attachment]
                }
            } else if (replyMode === 'thread' && inlineReplyPreview) {
                shouldOpenThread = true
                threadParentId = inlineReplyPreview.event.eventId

                // Create thread by setting threadId to the parent message's eventId
                messageOptions.threadId = threadParentId

                // Create a thread preview (truncated version of the original message)
                const threadPreviewText = inlineReplyPreview.eventContent.body
                    .substring(0, 1000)
                    .replace(/\n/g, ' ')
                    .trim()

                messageOptions.replyId = undefined
                messageOptions.replyPreview = undefined
                messageOptions.threadPreview = threadPreviewText
            } else if (replyToEventId) {
                // Direct reply
                messageOptions.replyId = replyToEventId
            }

            // Track analytics (same as useChannelSend)
            trackPostedMessage({
                spaceId: spaceId ?? spaceIdFromPath,
                channelId,
                threadId: messageOptions.threadId || threadId,
                canReplyInline,
                replyToEventId,
                messageType: getPostedMessageType('', {
                    messageType: messageOptions?.messageType,
                    filesCount: 0,
                    hasTicker: false,
                }),
                ...getCachedSpaceDataForAnalytics(),
            })

            // Setup callback to open thread panel if needed
            if (shouldOpenThread && threadParentId) {
                const originalCallback = messageOptions.onLocalEventAppended
                messageOptions.onLocalEventAppended = async (localId: string) => {
                    // Call original callback if it exists
                    if (originalCallback) {
                        await originalCallback(localId)
                    }
                    // Open thread panel
                    onOpenMessageThread(threadParentId!)
                }
            }

            // Send message with proper spaceId handling
            const finalSpaceId = spaceId ?? spaceIdFromPath
            if (finalSpaceId) {
                sendMessage(channelId, '', {
                    parentSpaceId: finalSpaceId,
                    ...messageOptions,
                })
            } else {
                sendMessage(channelId, '', messageOptions)
            }

            // Reset reply context if this was a reply
            if (replyToEventId) {
                resetReplyToMessageContext()
            }
        },
        [
            closeCard,
            sendMessage,
            channelId,
            spaceId,
            spaceIdFromPath,
            threadId,
            threadPreview,
            replyMode,
            replyToEventId,
            inlineReplyPreview,
            getChannelEvent,
            canReplyInline,
            resetReplyToMessageContext,
            onOpenMessageThread,
        ],
    )

    const backgroundColor = useMemo(() => theme && themes[theme].layer.level1, [theme])

    return (
        <Box height={isTouch ? '100svh' : '420'}>
            <Box
                padding="sm"
                height={isTouch ? '100%' : '400'}
                background={isTouch ? 'level1' : 'level2'}
                borderRadius="sm"
                style={{ width }}
                boxShadow={isTouch ? undefined : 'dropdown'}
                gap="sm"
            >
                <GiphySearchBar />
                <Box scroll>
                    {isFetching && <Loader />}
                    <Suspense fallback={<Loader />}>
                        <ErrorBoundary fallback={<ErrorFallback />}>
                            <Grid
                                hideAttribution
                                noLink
                                key={query}
                                width={gridWidth}
                                columns={2}
                                className={atoms({
                                    visibility: isFetching ? 'hidden' : 'visible',
                                })}
                                gutter={gutterWidth}
                                backgroundColor={backgroundColor}
                                fetchGifs={fetchGifs}
                                loader={() => <Loader />}
                                onGifClick={onGifClick}
                            />
                        </ErrorBoundary>
                    </Suspense>
                </Box>
            </Box>
        </Box>
    )
})
GiphyPickerCard.displayName = 'GiphyPickerCard'

// Simple Error Boundary component
class ErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    componentDidCatch(error: Error) {
        console.error('Error in Giphy component:', error)
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback
        }
        return this.props.children
    }
}
